import React from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatIDR, CUSTOMER_TYPE } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Download, Upload } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const empty = {
  name: "", type: "individual", pic_name: "", phone: "", whatsapp: "", email: "",
  billing_address: "", delivery_address: "", npwp: "", credit_limit: 0, default_discount: 0, notes: "",
};

export default function CustomersPage() {
  const [rows, setRows] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState(empty);
  const [editId, setEditId] = React.useState(null);

  const load = React.useCallback(async () => {
    const { data } = await api.get("/customers", { params: q ? { q } : {} });
    setRows(data);
  }, [q]);
  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editId) await api.patch(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      toast.success("Tersimpan"); setOpen(false); setEditId(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const edit = (c) => { setForm(c); setEditId(c.id); setOpen(true); };

  const deleteCustomer = async (cid) => {
    try { await api.delete(`/customers/${cid}`); toast.success("Pelanggan dinonaktifkan"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Pelanggan</h1>
          <p className="text-sm text-neutral-500">Database pelanggan aktif.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input placeholder="Cari nama / telp…" value={q} onChange={(e) => setQ(e.target.value)} className="w-56" data-testid="customer-search" />
          <Button variant="outline" size="sm" onClick={async () => {
            const res = await api.get("/export/customers", { responseType: "blob" });
            const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = "customers.csv"; a.click();
          }} data-testid="export-customers"><Download className="h-4 w-4 mr-1" />Ekspor</Button>
          <Button variant="outline" size="sm" onClick={() => document.getElementById("cust-import-file")?.click()} data-testid="import-customers"><Upload className="h-4 w-4 mr-1" />Impor</Button>
          <input id="cust-import-file" type="file" accept=".csv" className="hidden" onChange={async (e) => {
            const file = e.target.files?.[0]; if (!file) return;
            const fd = new FormData(); fd.append("file", file);
            try {
              const { data } = await api.post("/import/customers", fd, { headers: { "Content-Type": "multipart/form-data" } });
              toast.success(`Impor selesai: ${data.created} pelanggan dibuat${data.errors?.length ? `, ${data.errors.length} error` : ""}`);
              load();
            } catch (err) { toast.error(formatApiError(err)); }
            e.target.value = "";
          }} />
          <a href="#" onClick={async (e) => { e.preventDefault(); const res = await api.get("/export/customers/template", { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = "customers_template.csv"; a.click(); }} className="text-xs text-green-700 hover:underline" data-testid="download-template">Template</a>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700" data-testid="btn-new-customer"><Plus className="h-4 w-4 mr-1" /> Pelanggan Baru</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editId ? "Edit Pelanggan" : "Pelanggan Baru"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="c-name" /></div>
                <div><Label>Tipe</Label>
                  <select className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="c-type">
                    {Object.entries(CUSTOMER_TYPE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div><Label>PIC</Label><Input value={form.pic_name || ""} onChange={(e) => setForm({ ...form, pic_name: e.target.value })} /></div>
                <div><Label>Telepon</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="c-phone" /></div>
                <div><Label>WhatsApp</Label><Input value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="col-span-2"><Label>Alamat Billing</Label><Textarea rows={2} value={form.billing_address || ""} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} /></div>
                <div><Label>NPWP</Label><Input value={form.npwp || ""} onChange={(e) => setForm({ ...form, npwp: e.target.value })} /></div>
                <div><Label>Credit Limit</Label><Input type="number" value={form.credit_limit || 0} onChange={(e) => setForm({ ...form, credit_limit: +e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-green-600 hover:bg-green-700" data-testid="save-customer">Simpan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full data-table">
          <thead><tr>
            <th className="text-left">Kode</th>
            <th className="text-left">Nama</th>
            <th className="text-left">Tipe</th>
            <th className="text-left">PIC / Telepon</th>
            <th className="text-right">Credit Limit</th>
            <th></th>
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                <td className="font-mono text-xs">{c.code}</td>
                <td className="font-medium">{c.name}</td>
                <td className="text-xs">{CUSTOMER_TYPE[c.type] || c.type}</td>
                <td className="text-xs">{c.pic_name || "-"}<br /><span className="text-neutral-500">{c.phone || "-"}</span></td>
                <td className="text-right">{formatIDR(c.credit_limit)}</td>
                <td className="text-right whitespace-nowrap">
                  <button className="text-green-700 text-xs hover:underline mr-3" onClick={() => edit(c)} data-testid={`edit-customer-${c.id}`}>Edit</button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="text-red-600 text-xs hover:underline" data-testid={`delete-customer-${c.id}`}>Hapus</button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Hapus pelanggan {c.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Pelanggan akan ditandai sebagai <b>blacklisted</b>. Data booking, invoice, dan pembayaran historis tetap tersimpan. Anda dapat mengaktifkan kembali via edit (hapus centang blacklist).
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteCustomer(c.id)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-customer-${c.id}`}>Ya, Hapus</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-sm text-neutral-500">Belum ada pelanggan.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
